/**
 * BFF app config for dashboard-sonarprint.
 *
 * Assembles the dashboard-specific configuration consumed by the shared BFF glue in
 * `@group-link-one/gl-ui/sonarprint` (`SPBFFProvider` / `SPBFFManager`) — env, the
 * route→slug map, branding/theme key, and the app-level bridges (auth token, feature
 * flags, impersonation). Optional fields (language map, What's New copy, action URL
 * params, tours registry) fall back to gl-ui defaults; SonarPrint has no guided tours
 * yet, so no `toursRegistry` is provided.
 *
 * @module BFF
 */

import type { BFFAppConfig } from '@group-link-one/gl-ui/sonarprint';
import { useAuth, useGLFlags } from '@group-link-one/gl-utils';

import { resolveActiveBranding } from '@/Branding/BrandingRegistry';
import { env } from '@/env';

import { BRANDING_THEME_STORAGE_KEY } from './constants';
import { BFF_PAGE_SLUG_BY_ROUTE } from './pages.config';
import type { PageSlug } from './types';

/** Impersonation bridge — derive from the token's user (canonical BFF signal). */
const useImpersonation = () => {
  const { user } = useAuth();
  return { isImpersonating: Boolean(user?.impersonator_id) };
};

export const bffConfig: BFFAppConfig<PageSlug> = {
  env: {
    bffUrl: env.VITE_BFF_URL,
    branding: resolveActiveBranding(),
    version: __PACKAGE_VERSION__,
  },
  bridges: {
    useAuth,
    useFlags: useGLFlags,
    useUser: useImpersonation,
  },
  pageSlugByRoute: BFF_PAGE_SLUG_BY_ROUTE,
  brandingThemeStorageKey: BRANDING_THEME_STORAGE_KEY,
};
